import { Box, IconButton, makeStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import React, { useState } from "react";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil";

import { CustomIconButton } from "@/components/CustomIconButton";
import { OfferApproveModal } from "@/components/offer/ApproveModal";
import CustomPagination from "@/components/Pagination";
import { ManagedPropertyList } from "@/components/property/ManagedPropertyList";
import { PropertyEmptyState } from "@/components/property/PropertyEmptyState";
import { ErrorPanel } from "@/components/StatePanels";
import { Toast } from "@/components/Toast";
import { useDevice } from "@/hooks/useDevice";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { NavigationPath } from "@/utilities/enums";
import { calculatePagination } from "@/utilities/pagination";
import { getActualYieldRateByProperty } from "@/utilities/yield";
import { PropertyManagementListSnackbarState, SelectedPropertyId, SelectedPropertyIds } from "@/view_models/atoms";
import { PropertyIdToThisMonthlyBalanceSheet } from "@/view_models/balancesheetSelectors";
import { OwnedPropertiesWithSample } from "@/view_models/propertySelectors";

import MsinsBanner from "@assets/image/msinsBanner_fi_own.png";

import { AddNewPropertyModal } from "./components/AddNewPropertyModal";

const useStyles = makeStyles({
  image: { width: "100%", objectFit: "contain" },
  floatingBanner: {
    position: "fixed",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    width: 400,
    maxWidth: "calc(100vw - 32px)",
    zIndex: 1000,
  },
  closeButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.grey[600],
    position: "absolute",
    top: -16,
    right: -16,
    width: 32,
    height: 32,
  },
  error: { padding: "32px 24px", margin: "auto", maxWidth: 560 },
});
export const ManagementOwnPage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { isMobile } = useDevice();
  const [snackbarState, setSnackbarState] = useRecoilState(PropertyManagementListSnackbarState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  const properties = useRecoilValue(OwnedPropertiesWithSample);
  const sheets = useRecoilValueLoadable(PropertyIdToThisMonthlyBalanceSheet(null));
  const [currentPage, setCurrentPage] = useState(1);
  const { currentProperties, totalPages } = calculatePagination(properties, currentPage);
  const onClickAddProperty = () => setIsModalOpen(true);
  const onClickMsinsBanner = () =>
    window.open(
      "https://reg34.smp.ne.jp/regist/is?SMPFORM=ohme-melepd-f44d1d706291fe46098e3020dc663495",
      "_blank",
      "noopener,noreferrer"
    );
  const onClickPropertyDetail = (id: number) => {
    setSelectedPropertyId(id);
    setSelectedPropertyIds([id]);
    history.push("/management/own/detail");
  };
  return (
    <BaseTemplate
      headerTitle={isMobile ? "保有物件" : undefined}
      navigationPath={NavigationPath.PROPERTY_MANAGEMENT_OWNED}
      isDense
      isPcV2
    >
      {sheets.state === "hasError" ? (
        <Box className={classes.error}>
          <ErrorPanel
            title="物件を読み込めませんでした"
            description={"通信状況を確認して、\nもう一度お試しください。"}
            retryLabel="再読み込み"
            onRetry={() => window.location.reload()}
          />
        </Box>
      ) : sheets.state !== "loading" && properties.length === 0 ? (
        <PropertyEmptyState type="owned" onClick={onClickAddProperty} />
      ) : (
        <>
          <ManagedPropertyList
            properties={isMobile ? currentProperties : properties}
            count={properties.length}
            loading={sheets.state === "loading"}
            getActualYield={(holder) =>
              getActualYieldRateByProperty(holder, {
                annualPropertyTax:
                  sheets.state === "hasValue"
                    ? sheets.contents.get(holder.id)?.result.property_tax_of_last_year
                    : undefined,
              })
            }
            onSelect={onClickPropertyDetail}
            onAdd={onClickAddProperty}
          >
            {(() => {
              const now = new Date(new Date().toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" }));
              return now >= new Date("2025-07-01T00:00:00+09:00") ? (
                isMobile ? (
                  <Box mb={1} position="relative">
                    <Box onClick={onClickMsinsBanner} style={{ cursor: "pointer" }}>
                      <img className={classes.image} src={MsinsBanner} alt="Msins own banner" />
                    </Box>
                  </Box>
                ) : (
                  showBanner && (
                    <Box className={classes.floatingBanner}>
                      <IconButton className={classes.closeButton} onClick={() => setShowBanner(false)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <Box onClick={onClickMsinsBanner} style={{ cursor: "pointer" }}>
                        <img className={classes.image} src={MsinsBanner} alt="Msins own banner" />
                      </Box>
                    </Box>
                  )
                )
              ) : null;
            })()}
          </ManagedPropertyList>
          {isMobile && sheets.state === "hasValue" && (
            <>
              <Box px={3} pb={2}>
                <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              </Box>
              <CustomIconButton aria-label="物件を登録する" onClick={() => setIsModalOpen(true)} float>
                <AddIcon />
              </CustomIconButton>
            </>
          )}
        </>
      )}
      <AddNewPropertyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <Toast
        open={snackbarState.isOpen}
        variant={snackbarState.status}
        message={snackbarState.message}
        onClose={() => setSnackbarState({ ...snackbarState, isOpen: false })}
      />
      {/* {isMsinsLeadModalOpen && <MsinsLeadModal onClose={() => setIsMsinsLeadModalOpen(false)} onAccept={() => onClickMsinsInterest()} />}
      {isMsinsCheckModalOpen && (
        <MsinsCheckModal onClose={() => setIsMsinsCheckModalOpen(false)} onAccept={() => onClickAccept()} />
      )} */}
      {isApproveModalOpen && <OfferApproveModal offerType="msins_own" onClose={() => setIsApproveModalOpen(false)} />}
    </BaseTemplate>
  );
};
